import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Configure axios defaults
// In production (Firebase Hosting), use relative URLs which will be rewritten to the function
// In development, use localhost or the environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');
axios.defaults.baseURL = API_BASE_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage if token exists (for persistence across page reloads)
  const getInitialUser = () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      // Try to get user from localStorage if available
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  };

  const [user, setUser] = useState(getInitialUser());
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const justLoggedIn = useRef(false);

  // Set up axios interceptor for token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log('checkAuth called - token:', token ? 'set' : 'not set', 'justLoggedIn:', justLoggedIn.current, 'user:', user);
      
      if (token) {
        // If we just logged in, skip the check to avoid race condition
        if (justLoggedIn.current) {
          console.log('Just logged in, skipping checkAuth');
          justLoggedIn.current = false;
          setLoading(false);
          return;
        }
        
        // If user is already set (from localStorage or previous check), verify it's still valid
        if (user) {
          console.log('User already set, verifying with API call');
          try {
            const response = await axios.get('/auth/me');
            console.log('Auth verification successful, user:', response.data);
            // Update user in case it changed
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            setLoading(false);
            return;
          } catch (error) {
            console.error('Auth verification failed:', error);
            // If verification fails, clear everything
            if (error.response?.status === 401 || error.response?.status === 403) {
              console.log('Auth error, clearing token and user');
              setToken(null);
              setUser(null);
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              delete axios.defaults.headers.common['Authorization'];
            }
            setLoading(false);
            return;
          }
        }
        
        // No user set, try to get from API
        try {
          console.log('Calling /auth/me to verify token');
          const response = await axios.get('/auth/me');
          console.log('Auth check successful, user:', response.data);
          setUser(response.data);
          // Also store in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.error('Auth check failed:', error);
          // Only clear if it's a real auth error (401, 403), not network errors
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('Auth error, clearing token and user');
            setToken(null);
            setUser(null);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
          }
        }
      } else {
        console.log('No token, setting loading to false');
        // Clear user if no token
        if (user) {
          setUser(null);
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]); // Remove user from dependencies to avoid infinite loop

  const login = async (username, password) => {
    try {
      const response = await axios.post('/auth/login', {
        username,
        password
      });

      const { access_token, user: userData } = response.data;
      
      // Mark that we just logged in to prevent checkAuth from clearing user
      // Do this BEFORE setting token to ensure it's set before useEffect runs
      justLoggedIn.current = true;
      console.log('justLoggedIn set to true');
      
      // Set user FIRST, then token, to ensure user is set before checkAuth runs
      // Also store in localStorage for persistence
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      console.log('User set in AuthContext:', userData);
      
      // Set token - this will trigger checkAuth useEffect, but justLoggedIn will prevent it from clearing user
      setToken(access_token);
      localStorage.setItem('token', access_token);
      console.log('Token set in AuthContext');
      
      // Set loading to false to prevent ProtectedRoute from showing loading
      setLoading(false);
      console.log('Loading set to false, isAuthenticated should be:', !!userData);
      
      // Force a re-render by updating state synchronously
      // The return value will have the user data for immediate use
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      justLoggedIn.current = false;
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isUser = () => {
    return user && user.role === 'user';
  };

  const hasRole = (role) => {
    return user && user.role === role;
  };

  // Compute isAuthenticated - add logging for debugging
  const isAuthenticated = !!user;
  console.log('AuthContext render - user:', user, 'isAuthenticated:', isAuthenticated, 'token:', token ? 'set' : 'not set');

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    isAdmin,
    isUser,
    hasRole,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
